import React, { useContext, useEffect, useState } from 'react';
import {
  Platform,
  ToastAndroid,
  TouchableHighlight,
  View,
  StyleSheet,
} from 'react-native';

import DyteClient, { DyteParticipant } from '@dytesdk/web-core';
import { SvgXml } from 'react-native-svg';
import UserAvatar from 'react-native-user-avatar';
import DyteText from './DyteText';
import { IconPack, defaultIconPack } from '../lib/icons';
import { DyteI18n, useLanguage } from '../lib/lang';
import { Peer } from '../types/dyte-client';
import { useDyteSelector } from '@dytesdk/react-native-core';
import { FlagsmithFeatureFlags } from '../utils/flags';
import { DyteUIContext } from '../lib/context-provider/ui-context';

export default function ParticipantCard({
  meeting,
  participant,
  iconPack = defaultIconPack,
  t,
}: {
  meeting?: DyteClient;
  participant: Peer; //DyteParticipant | DyteSelf;
  iconPack?: IconPack;
  t?: DyteI18n;
}) {
  const [offsetFromTop, setOffset] = useState(0);
  const defaultLang = useLanguage();
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const [enableHostOptions, setEnableHostOptions] = useState(false);
  const { permissions } = meeting.self;
  const showHostControls =
    permissions.pinParticipant ||
    permissions.kickParticipant ||
    permissions.canAllowParticipantAudio ||
    permissions.canAllowParticipantVideo;
  const { storeStates, setStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  const stageStatus = participant.stageStatus;
  const stageEnabled = useDyteSelector((m) => m.self.permissions.stageEnabled);
  const shouldShowIcons =
    meeting.meta.viewType === 'GROUP_CALL'
      ? true
      : stageEnabled && stageStatus === 'ON_STAGE';
  const showAudioVideoIcons =
    meeting.meta.viewType === 'GROUP_CALL' ||
    (meeting.meta.viewType === 'WEBINAR' &&
      (meeting.stage.status === 'ON_STAGE' ||
        participant.stageStatus === 'ON_STAGE')) ||
    (meeting.meta.viewType === 'LIVESTREAM' &&
      (meeting.stage.status === 'ON_STAGE' ||
        participant.stageStatus === 'ON_STAGE'));
  useEffect(() => {
    if (storeStates.activeHostControlID !== participant.id)
      setEnableHostOptions(false);
  }, [participant.id, storeStates.activeHostControlID]);
  const styles = StyleSheet.create({
    participantCard: {
      // backgroundColor: 'white',
      zIndex: 2,
      paddingHorizontal: 14,
      paddingTop: 4,
      paddingBottom: 24,
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    participantCardLeft: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
    },
    participantCardRight: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    dropdownButton: {
      padding: 8,
    },
    hostControlContainer: {
      backgroundColor: colors.background[1000],
      position: 'absolute',
      right: 10,
      top: offsetFromTop + 45,
      zIndex: 3,
      borderRadius: 4,
    },
    controls: {
      margin: 4,
      backgroundColor: colors.background[1000],
    },
    participantNameText: {
      marginLeft: 16,
    },
    micIcon: {
      marginRight: 8,
    },
    options: {
      flexDirection: 'row',
      padding: 8,
    },
    optionText: {
      marginLeft: 10,
    },
  });
  const acceptStageRequests =
    meeting.self.permissions.acceptStageRequests &&
    meeting.self.permissions.stageEnabled &&
    (meeting?.__internals__.features.hasFeature(
      FlagsmithFeatureFlags.ADMIN_CANTREMOVE_ADMIN
    ) !== true ||
      participant?.presetName !== 'webinar_admin') &&
    (meeting?.__internals__.features.hasFeature(
      FlagsmithFeatureFlags.CANTINVITE_VIEWER
    ) !== true ||
      participant?.presetName !== 'webinar_viewer');
  const canKickParticipant =
    meeting.self.permissions.kickParticipant &&
    meeting?.__internals__.features.hasFeature(
      FlagsmithFeatureFlags.DISABLE_KICKING
    ) !== true &&
    (meeting?.__internals__.features.hasFeature(
      FlagsmithFeatureFlags.ADMIN_CANTREMOVE_ADMIN
    ) !== true ||
      participant?.presetName !== 'webinar_admin');
  return (
    <>
      <View
        style={styles.participantCard}
        onLayout={(event) => {
          const layout = event.nativeEvent.layout;
          setOffset(layout.y);
        }}
      >
        <View style={styles.participantCardLeft}>
          <UserAvatar
            size={40}
            src={participant.picture}
            name={participant.name ?? 'TT'}
            bgColor={colors.brand[300]}
          />
          <DyteText style={styles.participantNameText} size="lg">
            {participant.name} {participant.id === meeting.self.id && '(you)'}
          </DyteText>
        </View>
        {showAudioVideoIcons && (
          <View style={styles.participantCardRight}>
            {shouldShowIcons && (
              <SvgXml
                xml={(!participant.audioEnabled
                  ? iconPack.mic_off
                  : iconPack.mic_on
                ).replace(
                  'currentColor',
                  participant.audioEnabled ? colors.text : colors.danger
                )}
                width={25}
                height={25}
              />
            )}
            {/* eslint-disable-next-line react-native/no-inline-styles*/}
            <View style={{ width: 8 }} />
            {shouldShowIcons && (
              <SvgXml
                xml={(!participant.videoEnabled
                  ? iconPack.video_off
                  : iconPack.video_on
                ).replace(
                  'currentColor',
                  participant.videoEnabled ? colors.text : colors.danger
                )}
                width={25}
                height={25}
              />
            )}
            {showHostControls && (
              <TouchableHighlight
                underlayColor={colors.background[800]}
                onPress={() => {
                  // console.log(
                  //   participant.name,
                  //   offsetFromTop,
                  //   offsetFromTop - 80
                  // );
                  if (enableHostOptions) {
                    setEnableHostOptions(false);
                    setStates({
                      ...storeStates,
                      activeHostControlID: 'none',
                    });
                  } else {
                    // if (storeStates.activeHostControlID === 'none') {
                    setEnableHostOptions(true);
                    setStates({
                      ...storeStates,
                      activeHostControlID: participant.id,
                    });
                    // }
                  }
                }}
                style={styles.dropdownButton}
              >
                <SvgXml
                  xml={iconPack.more_vertical.replace(
                    'currentColor',
                    colors.text
                  )}
                  width={20}
                  height={20}
                />
              </TouchableHighlight>
            )}
          </View>
        )}
      </View>
      {showHostControls && enableHostOptions && (
        <View style={styles.hostControlContainer}>
          {permissions.pinParticipant &&
            (meeting.meta.viewType !== 'WEBINAR' ||
              participant.stageStatus === 'ON_STAGE') && (
              <TouchableHighlight
                underlayColor={colors.background[800]}
                onPress={() => {
                  participant.isPinned
                    ? participant.unpin()
                    : participant.pin();
                }}
                style={styles.controls}
              >
                <View style={styles.options}>
                  <SvgXml
                    xml={iconPack.pin.replace('currentColor', colors.text)}
                    width={20}
                    height={20}
                  />
                  <DyteText size={'sm'} style={styles.optionText}>
                    {participant.isPinned ? t('unpin') : t('pin')}
                  </DyteText>
                </View>
              </TouchableHighlight>
            )}
          {acceptStageRequests && participant.id !== meeting.self.id && (
            <TouchableHighlight
              underlayColor={colors.background[800]}
              onPress={() => {
                participant.stageStatus === 'ON_STAGE'
                  ? meeting.stage.kick([participant.userId])
                  : meeting.stage.grantAccess([participant.userId]);
              }}
              style={styles.controls}
            >
              <View style={styles.options}>
                <SvgXml
                  xml={(participant.stageStatus === 'ON_STAGE'
                    ? iconPack.leave_stage
                    : iconPack.join_stage
                  ).replace('currentColor', colors.text)}
                  width={20}
                  height={20}
                />
                <DyteText size={'sm'} style={styles.optionText}>
                  {participant.stageStatus === 'ON_STAGE'
                    ? t('stage.remove_from_stage')
                    : t('stage.add_to_stage')}
                </DyteText>
              </View>
            </TouchableHighlight>
          )}
          {permissions.canDisableParticipantAudio &&
            participant.audioEnabled && (
              <TouchableHighlight
                underlayColor={colors.background[800]}
                onPress={() => {
                  participant
                    .disableAudio()
                    .catch((err) =>
                      Platform.OS === 'ios'
                        ? console.log(err)
                        : ToastAndroid.show(err.toString(), ToastAndroid.SHORT)
                    );
                }}
                style={styles.controls}
              >
                <View style={styles.options}>
                  <SvgXml
                    xml={iconPack.speaker_off.replace(
                      'currentColor',
                      colors.text
                    )}
                    width={20}
                    height={20}
                  />
                  <DyteText size={'sm'} style={styles.optionText}>
                    {t('mute')}
                  </DyteText>
                </View>
              </TouchableHighlight>
            )}
          {permissions.canDisableParticipantVideo &&
            participant.videoEnabled && (
              <TouchableHighlight
                underlayColor={colors.background[800]}
                onPress={() => {
                  participant
                    .disableVideo()
                    .catch((err) =>
                      Platform.OS === 'ios'
                        ? console.log(err)
                        : ToastAndroid.show(err.toString(), ToastAndroid.SHORT)
                    );
                }}
                style={styles.controls}
              >
                <View style={styles.options}>
                  <SvgXml
                    xml={iconPack.video_off.replace(
                      'currentColor',
                      colors.text
                    )}
                    width={20}
                    height={20}
                  />
                  <DyteText size={'sm'} style={styles.optionText}>
                    {t('participants.turn_off_video')}
                  </DyteText>
                </View>
              </TouchableHighlight>
            )}
          {canKickParticipant && participant.id !== meeting.self.id && (
            <TouchableHighlight
              underlayColor={colors.background[800]}
              onPress={() => {
                (participant as DyteParticipant)
                  .kick()
                  .catch((err) =>
                    Platform.OS === 'ios'
                      ? console.log(err)
                      : ToastAndroid.show(err.toString(), ToastAndroid.SHORT)
                  );
              }}
              style={styles.controls}
            >
              <View style={styles.options}>
                <SvgXml
                  xml={iconPack.dismiss.replace(
                    'currentColor',
                    colors.danger
                  )}
                  width={20}
                  height={20}
                />
                <DyteText
                  style={{
                    ...styles.optionText,
                    color: colors.danger,
                  }}
                >
                  {t('kick')}
                </DyteText>
              </View>
            </TouchableHighlight>
          )}
        </View>
      )}
    </>
  );
}
